import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import Brand from '@/components/common/Brand';
import { useAuth } from '@/lib/auth';
import { WalletChip, AccountMenu, UnreadBadge } from '@/components/common/HeaderControls';

const PUBLIC_NAV_LINKS = [
  { href: '#how', label: 'How it works' },
  { href: '#services', label: 'Experiences' },
  { href: '#safety', label: 'Trust & safety' },
  { href: '#stories', label: 'Stories' },
  { href: '#faq', label: 'FAQ' },
];

const SIGNED_IN_NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/#services', label: 'Services' },
  { to: '/companions', label: 'Companions' },
  { to: '/bookings', label: 'Bookings' },
  { to: '/messages', label: 'Messages' },
];

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { session } = useAuth();
  const isHome = location.pathname === '/';
  const isSignedIn = Boolean(session);

  const getHref = (hash: string) => {
    return isHome ? hash : `/${hash}`;
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="flex min-h-dvh flex-col bg-white text-ink antialiased">
      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md shadow-xs"
      >

        <nav
          className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8"
          aria-label="Main Navigation"
        >
          <Brand to="/" />

          {/* Desktop Nav - Authenticated vs Public */}
          {isSignedIn ? (
            <div className="hidden items-center gap-1.5 md:flex">
              {SIGNED_IN_NAV_LINKS.map((item) => {
                const active = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
                      active ? 'bg-brand-soft text-brand' : 'text-slate2 hover:text-navy'
                    }`}
                  >
                    <span className="inline-flex items-center">
                      {item.label}
                      {item.to === '/messages' && <UnreadBadge />}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ) : (
            <div className="hidden items-center gap-7 text-sm font-semibold text-slate2 md:flex">
              {PUBLIC_NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={getHref(link.href)}
                  className="transition-colors duration-150 hover:text-navy"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Desktop Right CTAs */}
          {isSignedIn ? (
            <div className="hidden items-center gap-3 md:flex">
              <WalletChip />
              <AccountMenu />
            </div>
          ) : (
            <div className="hidden items-center gap-3 md:flex">
              <Link
                to="/login"
                className="rounded-full px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-brand-soft"
              >
                Log in
              </Link>
              <a
                href={getHref('#pricing')}
                className="btn-grad rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
              >
                Browse partners
              </a>
            </div>
          )}

          {/* Mobile Menu Toggle Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="rounded-lg p-2 text-navy md:hidden hover:bg-mist"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 18L18 6M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </nav>

        {/* Quick jump anchor bar on Home page for signed-in users */}
        {isSignedIn && isHome && (
          <div className="border-t border-slate-100 bg-mist/90 px-5 py-2 text-xs">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 overflow-x-auto">
              <span className="font-bold text-slate2 uppercase tracking-wider text-[10px]">Jump to:</span>
              <div className="flex items-center gap-3">
                {PUBLIC_NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="font-semibold text-navy transition-colors hover:text-brand"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Dropdown Drawer */}
        {mobileMenuOpen && (
          <div className="border-t border-slate-200 bg-white px-5 pb-5 md:hidden animate-rise">
            <div className="flex flex-col gap-1 pt-3 text-base font-semibold text-ink">
              {isSignedIn ? (
                <>
                  {SIGNED_IN_NAV_LINKS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={closeMenu}
                      className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-mist"
                    >
                      <span>{item.label}</span>
                      {item.to === '/messages' && <UnreadBadge />}
                    </NavLink>
                  ))}
                  <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-3">
                    <WalletChip />
                    <AccountMenu />
                  </div>
                </>
              ) : (
                <>
                  {PUBLIC_NAV_LINKS.map((link) => (
                    <a
                      key={link.href}
                      href={getHref(link.href)}
                      onClick={closeMenu}
                      className="rounded-lg px-3 py-2.5 hover:bg-mist"
                    >
                      {link.label}
                    </a>
                  ))}
                  <a
                    href={getHref('#partner')}
                    onClick={closeMenu}
                    className="rounded-lg px-3 py-2.5 text-brand hover:bg-brand-soft"
                  >
                    Become a partner
                  </a>
                  <div className="mt-3 flex flex-col gap-2 pt-2 border-t border-slate-100">
                    <Link
                      to="/login"
                      onClick={closeMenu}
                      className="rounded-full border border-slate-200 py-2.5 text-center font-semibold text-navy hover:bg-mist"
                    >
                      Log in
                    </Link>
                    <a
                      href={getHref('#pricing')}
                      onClick={closeMenu}
                      className="btn-grad rounded-full py-3 text-center font-semibold text-white shadow-sm"
                    >
                      Browse partners
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* ── Main Content ──────────────────────────────────────────────────── */}
      <main className="flex-1">
        <Outlet />
      </main>



      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="bg-[linear-gradient(120deg,#0A1F63_0%,#24145E_50%,#4E1A86_100%)] text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-pink-300">Company</h3>
              <ul className="mt-4 space-y-3 text-sm text-blue-100/85">
                <li>
                  <a href={getHref('#how')} className="hover:text-white transition-colors">
                    About us
                  </a>
                </li>
                <li>
                  <a href={getHref('#faq')} className="hover:text-white transition-colors">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="mailto:Help@makemypartner.com" className="hover:text-white transition-colors">
                    Contact us
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    Support details
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-pink-300">Legal</h3>
              <ul className="mt-4 space-y-3 text-sm text-blue-100/85">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of service
                  </Link>
                </li>
                <li>
                  <Link to="/refunds" className="hover:text-white transition-colors">
                    Refund &amp; cancellation policy
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="hover:text-white transition-colors">
                    Fee structure
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-pink-300">Support</h3>
              <ul className="mt-4 space-y-3 text-sm text-blue-100/85">
                <li>
                  <a href={getHref('#faq')} className="hover:text-white transition-colors">
                    Help centre
                  </a>
                </li>
                <li>
                  <a href={getHref('#safety')} className="hover:text-white transition-colors">
                    Community guidelines
                  </a>
                </li>
                <li>
                  <a href={getHref('#safety')} className="hover:text-white transition-colors">
                    Trust &amp; safety
                  </a>
                </li>
                <li>
                  <Link to="/status" className="hover:text-white transition-colors">
                    System status
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-pink-300">Connect with us</h3>
              <div className="mt-4 flex gap-3">
                <a
                  href="#"
                  aria-label="Instagram"
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r=".8" fill="currentColor" />
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="Facebook"
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M15 3h-2a4 4 0 00-4 4v3H7v4h2v7h4v-7h2.5l.5-4h-3V7a1 1 0 011-1h2V3z" />
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="LinkedIn"
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M7 10v7M7 7v.01M11 17v-7M11 13a3 3 0 016 0v4" />
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="WhatsApp"
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 20l1.3-4A8 8 0 1112 20a8 8 0 01-4-1.1L4 20z" />
                  </svg>
                </a>
              </div>
              <p className="mt-5 text-sm text-blue-100/80">
                <a href="mailto:Help@makemypartner.com" className="hover:text-white transition-colors">
                  Help@makemypartner.com
                </a>
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-6 border-t border-white/15 pt-8 md:flex-row">
            <Link to="/" className="flex items-center gap-2.5" aria-label="MakeMyPartner Home">
              <span className="brand-ring grid size-8.5 place-items-center rounded-xl p-[2px] shadow-sm">
                <span className="grid size-full place-items-center rounded-[10px] bg-navy text-white">
                  <svg viewBox="0 0 24 24" className="size-4.5" fill="none" aria-hidden="true">
                    <path
                      d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="10" r="2.5" fill="currentColor" />
                  </svg>
                </span>
              </span>
              <span className="text-xl font-extrabold tracking-tight text-white">
                Make<span className="text-pink-400">my</span>partner
              </span>
            </Link>

            <div className="text-center text-sm text-blue-100/75 md:text-right">
              <p>© 2026 MakeMyPartner. All rights reserved.</p>
              <p className="mt-1 text-xs">All partners and members must be 18 or older. Made in India.</p>
              <p className="mt-2 max-w-xl text-xs leading-relaxed text-blue-100/60 md:ml-auto">
                MakeMyPartner offers verified activity companionship — it is not medical or mental health care.
                If you are in crisis, please call Tele-MANAS on 14416 (free, 24/7) or 112.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
